import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNote } from 'src/hook/components/useNote';
import { useLearning } from 'src/hook/data/useLearning';
import useProgress from 'src/hook/data/useProgress';
import { useSocial } from 'src/hook/data/useSocial';
import { useWordList } from 'src/hook/data/useWordList';
import { useWordTranslate } from 'src/hook/data/useWordTranslate';
import { useDeviceInfo } from 'src/hook/global/useDeviceInfo';
import { useToast } from 'src/hook/global/useToasts';
import usePopUp from 'src/hook/modal/usePopUp';
import useUser from '../../../hook/data/useUser';
import { useIFrameModal } from '../../../hook/modal/useIFrameModal';
import { GetRazorSiteUrl } from '../../../utils/URLUtils';
import BasicSection from './BasicSection/BasicSection';
import ButtonGroupSection from './ButtonGroupSection/ButtonGroupSection';
import LogoSection from './LogoSection/LogoSection';
import styles from './MainNavigation.module.scss';
import NavigationSection from './NavigationSection/NavigationSection';
import PronounceSection from './PronounceSection/PronounceSection';
import ThemeSection from './ThemeSection/ThemeSection';
import VideoSection from './VideoSection/VideoSection';

const MainNavigation: React.FC<{ toggleNavigation: () => void, show: boolean }> = ({ toggleNavigation, show }) => {
    const { t } = useTranslation();
    const { open } = useIFrameModal();
    const navigate = useNavigate();
    const { fetchUserData, userInfo, isProUser } = useUser();
    const toast = useToast();
    const { fetchTranslates } = useWordTranslate(false);
    const { fetchNoteData } = useNote(false);
    const { fetchSocialInfo } = useSocial(false);
    const [isLoading, setIsLoading] = useState(false);
    const { fetchLearningData } = useLearning(false);
    const { fetchAllWordListData } = useWordList();
    const { uploadProgressAtomic } = useProgress();
    const { showProPopUp } = usePopUp();
    const { sendDeviceInfo } = useDeviceInfo();


    useEffect(() => {
        const secondChild = document.body.children[1].children[0] as HTMLElement;
        const forthChild = document.body.children[1].children[0].children[0].children[0] as HTMLElement;
        if (show && secondChild && forthChild) {
            secondChild.style.overflowY = 'hidden';
            forthChild.style.overflowY = 'hidden';
        } else if (secondChild && forthChild) {
            secondChild.style.overflowY = 'auto';
            forthChild.style.overflowY = 'auto';
        }

        return () => {
            if (secondChild && forthChild) {
                secondChild.style.overflowY = 'auto';
                forthChild.style.overflowY = 'auto';
            }
        };
    }, [show]);

    const handleOpenModal = (url: string, title?: string) => {
        if (!isProUser() && url.includes('chrome')) {
            showProPopUp(t('Chrome Extension'),
                t('Add words to your learning list while reading online.'));
        }
        else {
            open(url, title);
            toggleNavigation();
        }
    };

    const handleSync = async () => {
        setIsLoading(true);
        await uploadProgressAtomic();
        const [user, learning] =
            await Promise.all([
                fetchUserData(userInfo?.Token!),
                fetchLearningData(userInfo?.Token!),
            ]);
        await Promise.all([
            fetchTranslates(user?.NativeLanguageCode),
            fetchNoteData(user?.UserId),
            fetchSocialInfo(user?.UserId!, user?.CountryCode),
            fetchAllWordListData(learning?.WordLists || []),
        ]);

        sendDeviceInfo();
        toggleNavigation();
        toast.showSuccess('Sync completed');
        setIsLoading(false);
    };


    if (!show) return null;


    return (
        <>
            {show && (
                <>
                    <div className={styles.overlay} onClick={toggleNavigation} />
                    <nav className={styles.mainNavigation}>
                        <LogoSection />

                        <NavigationSection icon="search" onClick={() => navigate('/search')} text={t('Search')} />

                        <ThemeSection />

                        <PronounceSection />

                        <BasicSection />

                        <NavigationSection
                            icon="user-check"
                            text={t('Account Settings')}
                            hasArrow
                            onClick={() => handleOpenModal(GetRazorSiteUrl('settings/account', userInfo?.Token), t('Account Settings'))}
                        />

                        <NavigationSection
                            icon="settings"
                            text={t('More Settings')}
                            hasArrow
                            onClick={() => handleOpenModal(GetRazorSiteUrl('settings/preferences', userInfo?.Token), t('Preferences'))}
                        />

                        <NavigationSection
                            icon="quote"
                            text={t('My Interests')}
                            hasArrow
                            onClick={() => handleOpenModal(GetRazorSiteUrl('profile/my-interest', userInfo?.Token), t('Personalise WordUp'))}
                        />

                        <NavigationSection
                            icon="chrome-extension"
                            onClick={() => handleOpenModal(GetRazorSiteUrl('profile/chrome', userInfo?.Token))}
                            text={t('Chrome Extension')}
                        />

                        <NavigationSection
                            icon="repeat"
                            onClick={handleSync}
                            text={t('Sync devices')}
                            isLoading={isLoading}
                        />

                        <NavigationSection
                            icon="tool"
                            onClick={() => handleOpenModal(GetRazorSiteUrl('support', userInfo?.Token))}
                            text={t('Support & feedback')}
                            hasArrow
                        />

                        <ButtonGroupSection />
                        <VideoSection handleOpenModal={handleOpenModal} />

                        <p className={styles.version}>{t('Version 6.5.10')}</p>
                    </nav >
                </>
            )}
        </>
    );
};

export default MainNavigation;